import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LeftOutlined, RightOutlined, UpOutlined } from "@ant-design/icons";
import { GemstoneGenericIcon } from "../../helper/gemImage";

// Reusable scrollable/expandable image-tile filter row (Gem Type / Shape),
// ported from Gemstone_Link_UI's MinedTab.js GemTypeFilter component so both
// filters share the same look/behavior: a horizontal strip that scrolls past
// VISIBLE_THRESHOLD items, with a "..." button to expand into a wrapped grid.
const VISIBLE_THRESHOLD = 8;
const SCROLL_STEP = 220;

const GemTypeShapeFilterRow = ({
  items,
  itemKeyField,
  selectedKeyField = itemKeyField,
  labelField = itemKeyField,
  selectedValues,
  label,
  onToggle,
  expanded,
  onExpandedChange,
  getImage,
  variant,
}) => {
  const { t } = useTranslation();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const rowRef = useRef(null);

  const overflowing = items && items.length > VISIBLE_THRESHOLD;

  const updateScrollState = () => {
    const el = rowRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    if (expanded) return;
    updateScrollState();
    const el = rowRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, items]);

  if (!items || items.length === 0) return null;

  const scrollBy = (delta) => {
    if (rowRef.current) {
      rowRef.current.scrollBy({ left: delta, behavior: "smooth" });
    }
  };

  return (
    <div className="col-lg-12 col-md-12 gemtype__filter">
      <div className="input__block mb-1">
        <label className="lbl__txt">{label}</label>
      </div>

      <div className="gemtype__row-wrap">
        <div
          className={["gemtype__row", expanded && "gemtype__row--wrap"]
            .filter(Boolean)
            .join(" ")}
          ref={expanded ? null : rowRef}
        >
          {items.map((item) => {
            const isSelected =
              selectedValues && selectedValues.includes(item[selectedKeyField]);
            const blockClass = [
              "gemtype__block",
              variant === "shape" && "gemtype__block--shape",
              isSelected && "selected",
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <button
                type="button"
                key={item[itemKeyField]}
                className={blockClass}
                onClick={() => onToggle(item)}
              >
                <span className="gemtype__block-image">
                  <img
                    src={(getImage && getImage(item)) || GemstoneGenericIcon}
                    alt=""
                  />
                </span>
                <span className="gemtype__block-label">{t(item[labelField])}</span>
              </button>
            );
          })}

          {expanded && overflowing && (
            <button
              type="button"
              className="gemtype__nav-btn gemtype__more-btn"
              aria-label={t("Show Less")}
              onClick={() => onExpandedChange(false)}
            >
              <UpOutlined />
            </button>
          )}
        </div>

        {!expanded && overflowing && (
          <div className="gemtype__nav-group">
            <button
              type="button"
              className="gemtype__nav-btn"
              aria-label={t("Scroll left")}
              disabled={!canScrollLeft}
              onClick={() => scrollBy(-SCROLL_STEP)}
            >
              <LeftOutlined />
            </button>

            <button
              type="button"
              className="gemtype__nav-btn"
              aria-label={t("Scroll right")}
              disabled={!canScrollRight}
              onClick={() => scrollBy(SCROLL_STEP)}
            >
              <RightOutlined />
            </button>

            <button
              type="button"
              className="gemtype__nav-btn gemtype__more-btn"
              aria-label={t("View More")}
              onClick={() => onExpandedChange(true)}
            >
              &hellip;
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GemTypeShapeFilterRow;
